/**
 * Astrologer Admin - Consultation Management
 * Manage chat, call, and video consultations
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Tooltip,
  CircularProgress,
  Avatar,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search,
  Visibility,
  Refresh,
  Chat,
  Phone,
  VideoCall,
  AccessTime,
  CurrencyRupee,
  Person,
  CalendarMonth,
  TrendingUp,
} from '@mui/icons-material';
import axios from 'axios';
import { api_urls } from '../../../utils/api-urls';
import { toast } from 'react-toastify';

const ConsultationManagement = () => {
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('today');
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const [stats, setStats] = useState({
    totalToday: 0,
    chatCount: 0,
    callCount: 0,
    videoCount: 0,
    totalRevenue: 0,
    avgDuration: 0
  });

  useEffect(() => {
    fetchConsultations();
    fetchStats();
  }, [typeFilter, statusFilter, dateFilter]);

  const fetchConsultations = async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter !== 'all') params.type = typeFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      params.dateRange = dateFilter;

      const response = await axios.get(`${api_urls}api/astrologer/admin/consultations`, { params });
      setConsultations(response.data?.consultations || []);
    } catch (error) {
      console.error('Error fetching consultations:', error);
      // Demo data
      setConsultations([
        {
          _id: '1',
          consultationId: 'CON001',
          type: 'chat',
          astrologer: {
            _id: 'ast1',
            astrologerName: 'Pandit Ramesh Sharma',
            photo: 'https://via.placeholder.com/50'
          },
          customer: {
            _id: 'cust1',
            name: 'Amit Kumar',
            phone: '9876543210'
          },
          startTime: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
          endTime: new Date(Date.now() - 1.5 * 60 * 60 * 1000).toISOString(),
          duration: 30,
          rate: 15,
          totalAmount: 450,
          platformFee: 90,
          astrologerEarning: 360,
          status: 'completed',
          rating: 5,
          feedback: 'Very accurate predictions',
          createdAt: new Date().toISOString(),
        },
        {
          _id: '2',
          consultationId: 'CON002',
          type: 'call',
          astrologer: {
            _id: 'ast2',
            astrologerName: 'Guru Lakshmi Devi',
            photo: 'https://via.placeholder.com/50'
          },
          customer: {
            _id: 'cust2',
            name: 'Priya Singh',
            phone: '9876543211'
          },
          startTime: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
          endTime: new Date(Date.now() - 0.5 * 60 * 60 * 1000).toISOString(),
          duration: 30,
          rate: 20,
          totalAmount: 600,
          platformFee: 120,
          astrologerEarning: 480,
          status: 'completed',
          rating: 4,
          feedback: 'Good guidance',
          createdAt: new Date().toISOString(),
        },
        {
          _id: '3',
          consultationId: 'CON003',
          type: 'video',
          astrologer: {
            _id: 'ast1',
            astrologerName: 'Pandit Ramesh Sharma',
            photo: 'https://via.placeholder.com/50'
          },
          customer: {
            _id: 'cust3',
            name: 'Rahul Verma',
            phone: '9876543212'
          },
          startTime: new Date().toISOString(),
          endTime: null,
          duration: 15,
          rate: 25,
          totalAmount: 375,
          platformFee: 75,
          astrologerEarning: 300,
          status: 'ongoing',
          rating: null,
          feedback: null,
          createdAt: new Date().toISOString(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${api_urls}api/astrologer/admin/consultations/stats`);
      setStats(response.data || { 
        totalToday: 156, 
        chatCount: 85, 
        callCount: 52, 
        videoCount: 19,
        totalRevenue: 45600,
        avgDuration: 18
      });
    } catch (error) {
      setStats({ 
        totalToday: 156, 
        chatCount: 85, 
        callCount: 52, 
        videoCount: 19,
        totalRevenue: 45600,
        avgDuration: 18
      });
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'chat': return <Chat sx={{ color: '#2196F3' }} />;
      case 'call': return <Phone sx={{ color: '#4CAF50' }} />;
      case 'video': return <VideoCall sx={{ color: '#9C27B0' }} />;
      default: return <Chat />;
    }
  };

  const getTypeChip = (type) => {
    const config = {
      chat: { color: '#e3f2fd', textColor: '#1976d2', label: 'Chat' },
      call: { color: '#e8f5e9', textColor: '#388e3c', label: 'Call' },
      video: { color: '#f3e5f5', textColor: '#7b1fa2', label: 'Video' },
    };
    const cfg = config[type] || config.chat;
    return (
      <Chip 
        icon={getTypeIcon(type)} 
        label={cfg.label} 
        size="small" 
        sx={{ bgcolor: cfg.color, color: cfg.textColor }} 
      />
    );
  };

  const getStatusChip = (status) => {
    const config = {
      ongoing: { color: 'info', label: 'Ongoing' },
      completed: { color: 'success', label: 'Completed' },
      cancelled: { color: 'error', label: 'Cancelled' },
      missed: { color: 'warning', label: 'Missed' },
    };
    const cfg = config[status] || config.completed;
    return <Chip size="small" color={cfg.color} label={cfg.label} />;
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hrs}h ${mins}m`;
  };

  const filteredConsultations = consultations.filter(con =>
    con.consultationId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    con.astrologer?.astrologerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    con.customer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    con.customer?.phone?.includes(searchTerm)
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: '#9C27B0' }}>
        <Chat sx={{ mr: 1, verticalAlign: 'middle' }} />
        Consultation Management
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ bgcolor: '#f3e5f5' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#9C27B0' }}>
                {stats.totalToday}
              </Typography>
              <Typography variant="body2" color="textSecondary">Today's Sessions</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ bgcolor: '#e3f2fd' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chat sx={{ color: '#1976d2' }} />
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2' }}>
                  {stats.chatCount}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">Chat Sessions</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Phone sx={{ color: '#388e3c' }} />
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#388e3c' }}>
                  {stats.callCount}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">Call Sessions</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ bgcolor: '#fce4ec' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VideoCall sx={{ color: '#c2185b' }} />
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#c2185b' }}>
                  {stats.videoCount}
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">Video Sessions</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ bgcolor: '#fff3e0' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#f57c00' }}>
                ₹{stats.totalRevenue.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="textSecondary">Today's Revenue</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card sx={{ bgcolor: '#e8eaf6' }}>
            <CardContent>
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#3f51b5' }}>
                {stats.avgDuration} min
              </Typography>
              <Typography variant="body2" color="textSecondary">Avg Duration</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search consultations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                label="Type"
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="chat">Chat</MenuItem>
                <MenuItem value="call">Call</MenuItem>
                <MenuItem value="video">Video Call</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="ongoing">Ongoing</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
                <MenuItem value="missed">Missed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateFilter}
                label="Date Range"
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="yesterday">Yesterday</MenuItem>
                <MenuItem value="week">Last 7 Days</MenuItem>
                <MenuItem value="month">Last 30 Days</MenuItem>
                <MenuItem value="all">All Time</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={6} md={2}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={() => { fetchConsultations(); fetchStats(); }}
              fullWidth
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Consultations Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Astrologer</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Platform Fee</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Rating</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredConsultations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  No consultations found
                </TableCell>
              </TableRow>
            ) : (
              filteredConsultations.map((con) => (
                <TableRow key={con._id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {con.consultationId}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {new Date(con.createdAt).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>{getTypeChip(con.type)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar src={con.astrologer?.photo} sx={{ width: 32, height: 32 }} />
                      <Typography variant="body2">{con.astrologer?.astrologerName}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{con.customer?.name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {con.customer?.phone}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AccessTime fontSize="small" color="action" />
                      {formatDuration(con.duration)}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} color="success.main">
                      ₹{con.totalAmount}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      @₹{con.rate}/min
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="warning.main">
                      ₹{con.platformFee}
                    </Typography>
                  </TableCell>
                  <TableCell>{getStatusChip(con.status)}</TableCell>
                  <TableCell>
                    {con.rating ? (
                      <Chip 
                        label={`${con.rating} ★`} 
                        size="small" 
                        sx={{ bgcolor: '#fff3e0', color: '#f57c00' }} 
                      />
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedConsultation(con);
                          setDetailDialogOpen(true);
                        }}
                      >
                        <Visibility />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#9C27B0', color: 'white' }}>
          Consultation Details - {selectedConsultation?.consultationId}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedConsultation && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Type</Typography>
                {getTypeChip(selectedConsultation.type)}
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Status</Typography>
                <Box>{getStatusChip(selectedConsultation.status)}</Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>Astrologer</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Avatar src={selectedConsultation.astrologer?.photo} />
                  <Typography>{selectedConsultation.astrologer?.astrologerName}</Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Customer</Typography>
                <Typography>{selectedConsultation.customer?.name}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {selectedConsultation.customer?.phone}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Duration</Typography>
                <Typography fontWeight={600}>{formatDuration(selectedConsultation.duration)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="textSecondary">Rate</Typography>
                <Typography fontWeight={600}>₹{selectedConsultation.rate}/min</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="textSecondary">Total Amount</Typography>
                <Typography fontWeight={600} color="success.main">₹{selectedConsultation.totalAmount}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="textSecondary">Platform Fee (20%)</Typography>
                <Typography fontWeight={600} color="warning.main">₹{selectedConsultation.platformFee}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="textSecondary">Astrologer Earning</Typography>
                <Typography fontWeight={600} color="primary.main">₹{selectedConsultation.astrologerEarning}</Typography>
              </Grid>
              {selectedConsultation.rating && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>Rating & Feedback</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Chip 
                      label={`${selectedConsultation.rating} ★`} 
                      sx={{ bgcolor: '#fff3e0', color: '#f57c00', fontWeight: 600 }} 
                    />
                    {selectedConsultation.feedback && (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        "{selectedConsultation.feedback}"
                      </Typography>
                    )}
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConsultationManagement;
